import { type FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import useStartYourApplicationEditableAsset from '@core/hooks/useStartYourApplicationEditableAsset';
import { selectViewCorrelationIdMap } from '@core/reducers/eventingSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import { validatePhone } from '@core/services/leadDelivery';

type DualCtaTestProps = {
  schoolId: string;
  className?: string;
};

type ReturnType = {
  DualCtaTestComponent: FC<DualCtaTestProps>;
  shouldApplyWrapperStyles: boolean;
};

const useDualCtaTest = (schoolId: string = ''): ReturnType => {
  const viewCorrelationIdMap = useSelector(selectViewCorrelationIdMap);
  const formInputs = useSelector(selectAllInputs);

  const { elementClicked, elementViewed } = useElementEvents({
    correlationId: viewCorrelationIdMap[schoolId],
  });

  const { isTestEnabled, dualCtaText, dualCtaEnabled, dualCtaLinkout } = useStartYourApplicationEditableAsset(schoolId);

  const buildPurdueUrl = async (baseUrl: string, formInputs: any): Promise<string | null> => {
    const { firstName, lastName, email, phone } = formInputs;
    const phoneValidationResponse = await validatePhone(phone.value);
    const formattedPhone: string = phoneValidationResponse?.phoneNumber ?? phone.value;
    const numericOnlyPhone: string = formattedPhone.replace(/\D/g, '');
    const encodedFirstName = encodeURIComponent(firstName.value);
    const encodedLastName = encodeURIComponent(lastName.value);
    const encodedEmail = encodeURIComponent(email.value);
    const encodedPhone = encodeURIComponent(numericOnlyPhone);

    return `${baseUrl}?FNAME=${encodedFirstName}&LNAME=${encodedLastName}&EMAIL=${encodedEmail}&PHONE=${encodedPhone}&SYSTUDENTID=&SOURCEAPP=affiliate`;
  };

  const DualCtaTestComponent: FC<DualCtaTestProps> = ({ schoolId, className }) => {
    const isPurdue = schoolId === '451';
    const isDualCtaTest = isTestEnabled && dualCtaEnabled;

    const applicationCtaBtn = useMemo(
      () => ({
        webElement: {
          elementType: 'linkout-cta',
          text: dualCtaText,
          location: 'drawer',
          htmlId: 'voyager-schedule-call-cta',
          name: 'application-cta',
        },
      }),
      []
    );

    const onDualCtaClick = async () => {
      if (isPurdue && dualCtaLinkout && formInputs) {
        const modifiedUrl = await buildPurdueUrl(dualCtaLinkout, formInputs);
        window.open(modifiedUrl || '', '_blank');
      } else {
        window.open(dualCtaLinkout || '', '_blank');
      }
      elementClicked({ ...applicationCtaBtn, outboundUrl: dualCtaLinkout });
    };

    useEffect(() => {
      if (applicationCtaBtn) {
        elementViewed(applicationCtaBtn);
      }
    }, [isDualCtaTest, applicationCtaBtn]);

    if (isDualCtaTest) {
      return (
        <button type="button" className={className || ''} onClick={onDualCtaClick}>
          {dualCtaText}
        </button>
      );
    }

    return <></>;
  };

  return {
    DualCtaTestComponent,
    shouldApplyWrapperStyles: (isTestEnabled && dualCtaEnabled) || false,
  };
};

export default useDualCtaTest;
